import type {
	Batch,
	LiveBoard,
	LiveOrder,
	OrderItem,
	ServiceRequest,
	ServiceRequestStatus,
} from "@luckymenu/core";

/**
 * The Orders tab (ux-design.md, Orders): New (QR batches waiting for a
 * decision), In progress (open and billed orders) and Service requests.
 * Pure functions over `staff.ordering.listLive`, shared by both staff apps.
 */

export type BatchCard = {
	batch: Batch;
	/** Items the diner asked for (none removed or cancelled yet while pending). */
	items: OrderItem[];
	/** Units, counting quantities. */
	itemCount: number;
	/** Sum of the lines before tax, in paise. */
	itemsTotalPaise: number;
	/** The table's open order this batch will join when accepted, if any. */
	joinsOrder: LiveOrder | null;
};

export type BoardView = {
	newBatches: BatchCard[];
	inProgress: LiveOrder[];
	/** Open ones first (they need someone), then acknowledged; oldest first within each. */
	requests: ServiceRequest[];
};

export const EMPTY_BOARD: LiveBoard = { orders: [], pendingBatches: [], serviceRequests: [] };

export function batchCard(batch: Batch, orders: readonly LiveOrder[]): BatchCard {
	const items = batch.items.filter((i) => i.status === "active");
	return {
		batch,
		items,
		itemCount: items.reduce((n, i) => n + i.quantity, 0),
		itemsTotalPaise: items.reduce((n, i) => n + i.lineTotalPaise, 0),
		joinsOrder:
			(batch.tableId && orders.find((o) => o.tableId === batch.tableId && o.status === "open")) ||
			null,
	};
}

/**
 * Decisions and request changes sent but not answered yet. They show at
 * once (optimistic); if the server refuses, they drop out of here and the
 * board shows the server's state again.
 */
export type PendingChanges = {
	/** Batches being accepted or rejected. */
	batchIds: ReadonlySet<string>;
	/** Service requests being acknowledged or resolved. */
	requests: ReadonlyMap<string, ServiceRequestStatus>;
};

export const NO_PENDING: PendingChanges = { batchIds: new Set(), requests: new Map() };

/** The board as it will be once the pending changes are through. */
export function withPending(board: LiveBoard, pending: PendingChanges): LiveBoard {
	if (pending.batchIds.size === 0 && pending.requests.size === 0) return board;
	let next: LiveBoard = {
		...board,
		pendingBatches: board.pendingBatches.filter((b) => !pending.batchIds.has(b.id)),
	};
	for (const [id, status] of pending.requests) next = withRequestStatus(next, id, status);
	return next;
}

/** Sections of the board. */
export function boardView(board: LiveBoard): BoardView {
	const rank = (s: ServiceRequestStatus) => (s === "open" ? 0 : 1);
	return {
		newBatches: board.pendingBatches
			.filter((b) => b.status === "pending")
			.map((b) => batchCard(b, board.orders)),
		inProgress: board.orders,
		requests: board.serviceRequests
			.filter((r) => r.status !== "resolved")
			.map((r, i) => ({ r, i }))
			.sort((a, b) => rank(a.r.status) - rank(b.r.status) || a.i - b.i)
			.map(({ r }) => r),
	};
}

/** What accepting without `removed` leaves. Accepting with nothing left is refused: reject instead. */
export function acceptPreview(card: BatchCard, removed: ReadonlySet<string>) {
	const kept = card.items.filter((i) => !removed.has(i.id));
	return {
		kept,
		keptCount: kept.reduce((n, i) => n + i.quantity, 0),
		keptTotalPaise: kept.reduce((n, i) => n + i.lineTotalPaise, 0),
		nothingLeft: kept.length === 0,
	};
}

// Cache changes after the server confirmed a decision, before the refetch lands.

export function withoutBatch(board: LiveBoard, batchId: string): LiveBoard {
	return { ...board, pendingBatches: board.pendingBatches.filter((b) => b.id !== batchId) };
}

export function withRequest(board: LiveBoard, request: ServiceRequest): LiveBoard {
	return {
		...board,
		serviceRequests:
			request.status === "resolved"
				? board.serviceRequests.filter((r) => r.id !== request.id)
				: board.serviceRequests.map((r) => (r.id === request.id ? request : r)),
	};
}

export function withRequestStatus(
	board: LiveBoard,
	id: string,
	status: ServiceRequestStatus,
): LiveBoard {
	const request = board.serviceRequests.find((r) => r.id === id);
	return request ? withRequest(board, { ...request, status }) : board;
}

/** Whole minutes from `iso` to `now`, never negative. */
export function minutesSince(iso: string, now: number): number {
	return Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
}

/** Minutes as hours and minutes for "time seated" and "waiting": `{ hours: 1, minutes: 5 }`. */
export function hoursAndMinutes(totalMinutes: number): { hours: number; minutes: number } {
	return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}
